import { BookOpen, Calendar, CheckSquare, ChevronDown, ChevronUp, Sparkles, Square } from "lucide-react";
import { useMemo, useState } from "react";
import { Pressable, StyleSheet, View, Text, Platform } from "react-native";
import { colors, semanticColors } from "../../../../styles/design-system";

export interface TodoItem {
  name: string;
  completed: boolean;
  aiSummary?: string;
  date?: Date | string | number;
  workspaceName?: string;
}

interface TodoCardProps extends TodoItem {
  toggleCompletion: (value: boolean) => void;
  discussTask?: () => void;
}

export const TodoCard = ({
  name,
  completed,
  toggleCompletion,
  date,
  aiSummary,
  discussTask,
  workspaceName,
}: TodoCardProps) => {
  const [isActive, setIsActive] = useState(Platform.OS === "web" ? false : true);
  const [isDiscussActive, setIsDiscussActive] = useState(Platform.OS === "web" ? false : true);
  const shouldShowExpand = useMemo(() => (aiSummary?.length ?? 0) > 180, [aiSummary]);
  const [expandSummary, setExpandSummary] = useState(!shouldShowExpand);

  const displayedSummary = useMemo(
    () => (!shouldShowExpand || expandSummary ? aiSummary : aiSummary?.substring(0, 150) + "..."),
    [shouldShowExpand, expandSummary, aiSummary]
  );

  const containerStyle = [styles.container, isActive && !completed && styles.activeCard];
  const cardNameStyle = [styles.cardName, completed && styles.isCompleteCardName];
  const discussTaskBtnStyle = [
    styles.discussTaskBtn,
    isActive && !completed && styles.showDiscussBtn,
    isDiscussActive && styles.activeDiscussBtn,
  ];

  const aiIconStyle = {
    ...styles.aiSummaryIcon,
    ...(completed ? styles.completedAISummaryIcon : {}),
  };

  const iconStyle = {
    ...styles.metaDataIcon,
    ...(completed ? styles.textLight : {}),
  };

  return (
    <View
      style={containerStyle}
      onMouseEnter={() => {
        setIsActive(true);
      }}
      onMouseLeave={() => {
        setIsActive(false);
      }}>
      <Pressable
        style={styles.completeButton}
        onPress={() => {
          toggleCompletion(!completed);
        }}>
        {completed ? (
          <CheckSquare size={20} style={styles.checkSquare} />
        ) : (
          <Square size={20} style={{ color: "#98a2b3" }} />
        )}
      </Pressable>
      <View style={styles.cardContentWrapper}>
        <Text style={cardNameStyle}>{name}</Text>
        {!!aiSummary && (
          <View style={styles.aiSummaryWrapper}>
            <Sparkles size={14} style={aiIconStyle} />
            <Text style={[styles.aiSummaryText, completed && styles.completedAISummaryText]}>
              {displayedSummary}
              {shouldShowExpand && (
                <Text>
                  <Pressable
                    onPress={() => {
                      setExpandSummary((prev) => !prev);
                    }}>
                    <Text style={styles.viewSummaryText}>
                      {expandSummary ? "Show Less" : "View Summary"}
                      {expandSummary ? (
                        <ChevronUp style={styles.viewSummaryText} size={12} />
                      ) : (
                        <ChevronDown style={styles.viewSummaryText} size={12} />
                      )}
                    </Text>
                  </Pressable>
                </Text>
              )}
            </Text>
          </View>
        )}
        <View style={styles.metaData}>
          {workspaceName != null && (
            <>
              <BookOpen size={14} style={iconStyle} />
              <Text style={[styles.metaDataText, completed && styles.textLight]}>{workspaceName}</Text>
            </>
          )}
          {workspaceName != null && date != null && (
            <View style={[styles.separator, completed && styles.backgroundTextLight]} />
          )}
          {date != null && (
            <>
              <Calendar size={14} style={iconStyle} />
              <Text style={styles.metaDataText}>
                {new Date(date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "2-digit" })}
              </Text>
            </>
          )}
        </View>
      </View>
      {!completed && discussTask != null && (
        <View style={styles.actionColumn}>
          <Pressable
            onPress={discussTask}
            style={discussTaskBtnStyle}
            onMouseEnter={() => {
              setIsDiscussActive(true);
            }}
            onMouseLeave={() => {
              setIsDiscussActive(false);
            }}>
            <Text style={styles.discussTaskText}>Discuss Task</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    gap: 16,
    padding: 16,
    borderRadius: 32,
    borderWidth: 1,
    borderColor: colors.grey[200],
    maxWidth: 900,
    backgroundColor: "#fff",
  },
  activeCard: {
    shadowColor: "rgb(16, 24, 40)",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.16,
    shadowRadius: 3,
    elevation: 3,
    borderColor: colors.grey[300],
  },
  completeButton: {
    height: 20,
  },
  checkSquare: {
    color: colors.brand[500],
  },
  cardContentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cardName: {
    color: "#101828",
    fontWeight: 600,
    fontSize: 14,
  },
  isCompleteCardName: {
    textDecorationLine: "line-through",
    textDecorationStyle: "solid",
    color: "#667085",
  },
  aiSummaryWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
  },
  aiSummaryIcon: {
    color: "#a855f7",
  },
  completedAISummaryIcon: {
    color: "#c084fc",
  },
  aiSummaryText: {
    flex: 1,
    color: "#475467",
    fontSize: 13,
    lineHeight: 21,
  },
  completedAISummaryText: {
    color: "#667085",
  },
  viewSummaryText: {
    marginLeft: 6,
    color: "#3e68fe",
  },
  metaData: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  separator: {
    marginHorizontal: 6,
    width: 4,
    height: 4,
    borderRadius: 4,
    backgroundColor: "#d0d5dd",
  },
  metaDataIcon: {
    color: "#475467",
  },
  metaDataText: {
    color: "#475467",
    fontSize: 12,
  },
  actionColumn: {},
  discussTaskBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: semanticColors.surface.white,
    opacity: 0,
    pointerEvents: "none",
  },
  showDiscussBtn: {
    opacity: 1,
    pointerEvents: "auto",
  },
  activeDiscussBtn: {
    backgroundColor: semanticColors.surface.light,
  },
  discussTaskText: {
    color: "#344054",
    fontSize: 12,
    fontWeight: 500,
  },
  textLight: {
    color: "#667085",
  },
  backgroundTextLight: {
    backgroundColor: "#667085",
  },
});
